import { useMemo } from 'react';

import { useInViewport } from '@cast/shared-layout-ui';
import { useClusterOverviewQuery } from '@cast/shared-cost-data-access';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { buildServiceHealthMetricsFromClusterOverview } from '@cast/features-cluster-shared-utils';

import { useClusterDashboardOverviewQueryArgs } from './useClusterDashboardOverviewQueryArgs';

export type ServiceHealthMetricView = {
  id: string;
  label: string;
  value: string;
  unit?: string;
  status: 'ok' | 'breached' | 'at_risk' | 'locked';
  infoTooltip?: string;
  lockedMessage?: string;
};

const SERVICE_HEALTH_INFO_TOOLTIPS: Partial<Record<string, string>> = {
  errRate: 'Failed request rate across monitored workloads.',
  oom: 'Number of out-of-memory kills detected in the cluster.',
};

export const useClusterDashboardServiceHealthData = () => {
  const inView = useInViewport();
  const { cluster } = useCluster();
  const { startTime, endTime, stepSeconds } =
    useClusterDashboardOverviewQueryArgs();

  const {
    data: overview,
    isLoading,
    error,
    refetch,
  } = useClusterOverviewQuery({
    clusterId: cluster.id,
    startTime,
    endTime,
    stepSeconds,
    enabled: inView,
  });

  const { metrics, breachCount, riskCount } = useMemo(
    () => buildServiceHealthMetricsFromClusterOverview(overview?.slo),
    [overview?.slo]
  );

  const metricViews = useMemo((): ServiceHealthMetricView[] => {
    return metrics.map((metric) => ({
      id: metric.id,
      label: metric.label,
      value: metric.value,
      unit: metric.unit,
      status: metric.status,
      infoTooltip: SERVICE_HEALTH_INFO_TOOLTIPS[metric.id],
    }));
  }, [metrics]);

  return {
    metrics: metricViews,
    breachCount,
    riskCount,
    isLoading,
    hasError: !!error,
    refetch,
  };
};
